import { type AnimationEvent, type CSSProperties, useEffect, useRef, useState } from "react";

import { cn } from "../foundations/cn.js";
import { OptionalImage } from "../foundations/OptionalImage.js";

export interface PackParadeProps {
  /** The packs on the turntable, in turn: each its art and the cards that drop into it (up to three). */
  packs: readonly { image: string; cards: readonly string[] }[];
  className?: string;
}

/** When each of the front pack's cards drops in, in seconds into its turn. */
const CARD_DELAYS = [1.5, 2.7, 3.9];
const OFFSETS = [-2, -1, 0, 1, 2] as const;

/** The pack `n` turns from the front, wrapping in both directions. */
function packAt<T>(packs: readonly T[], n: number): T | undefined {
  return packs[((n % packs.length) + packs.length) % packs.length];
}

/**
 * Packs on a turntable behind a section's words: the one in front fills with color as its cards
 * drop in, glows and glints, then the row turns to the next. It plays only while on screen and the
 * tab is visible. Reduced motion shows the front pack lit and still. Decorative: hidden from
 * assistive tech, and it never takes a pointer.
 */
export function PackParade({ packs, className }: PackParadeProps) {
  const root = useRef<HTMLDivElement>(null);
  const [turn, setTurn] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let offscreen = false;
    const update = () => setPaused(offscreen || document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      offscreen = !(entry?.isIntersecting ?? true);
      update();
    });
    observer.observe(el);
    document.addEventListener("visibilitychange", update);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  if (packs.length === 0) return null;
  // The front pack's turn ends with its own animation, so the row turns exactly when it is done.
  const onTurnEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === "pack-parade-center" && event.target === event.currentTarget) {
      setTurn((n) => n + 1);
    }
  };
  return (
    <div
      ref={root}
      aria-hidden
      data-paused={paused || undefined}
      className={cn("pack-parade", className)}
    >
      {OFFSETS.map((offset) => {
        const pack = packAt(packs, turn + offset);
        if (!pack) return null;
        const front = offset === 0;
        return (
          <div
            key={turn + offset}
            data-offset={offset}
            className="pack-parade-pack"
            style={{ "--parade-pack-image": `url("${pack.image}")` } as CSSProperties}
          >
            <div className="pack-parade-body" onAnimationEnd={front ? onTurnEnd : undefined}>
              <span className="pack-parade-glow" />
              {front &&
                pack.cards.slice(0, CARD_DELAYS.length).map((card, i) => (
                  <OptionalImage
                    // biome-ignore lint/suspicious/noArrayIndexKey: a card's place in the drop is its identity.
                    key={i}
                    src={card}
                    alt=""
                    className="pack-parade-card"
                    style={{ animationDelay: `${CARD_DELAYS[i]}s` }}
                  />
                ))}
              <OptionalImage
                src={pack.image}
                alt=""
                className="pack-parade-layer pack-parade-shell"
              />
              <span className="pack-parade-window">
                <OptionalImage src={pack.image} alt="" className="pack-parade-layer" />
              </span>
              <span className="pack-parade-glint" />
            </div>
          </div>
        );
      })}
    </div>
  );
}
